import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import SupportStickyButton from "../components/support-chatbot/SupportStickyButton";
import NewsletterBanner from "../components/newsletter/NewsletterBanner";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/useIsMobile";

// NOTE: invalid ":lang" segments are gated upstream by SupportedPublicLayout in
// App.tsx, which renders the 404 page before PublicLayout ever mounts.
const PublicLayout: React.FC = () => {
  const [showNewsletterBanner, setShowNewsletterBanner] = useState(false);
  const location = useLocation();
  const { isMobile } = useIsMobile(); // Detectar si es móvil

  useEffect(() => {
    // Don't show newsletter banner on login and signup pages (handle language-prefixed paths)
    const excludedPaths = ["/login", "/signup"];
    const supportedLngs = ["en", "es", "fr"];
    const stripLangFromPath = (path: string) => {
      const parts = path.split("/");
      if (parts.length > 1 && supportedLngs.includes(parts[1])) {
        const rest = "/" + parts.slice(2).join("/");
        return rest === "/" ? "/" : rest.replace(/\/+$/, "");
      }
      return path;
    };
    const currentPathNoLang = stripLangFromPath(location.pathname);
    const shouldExcludeBanner = excludedPaths.some(
      (path) =>
        currentPathNoLang === path || currentPathNoLang.startsWith(path + "/")
    );

    if (shouldExcludeBanner) {
      setShowNewsletterBanner(false);
      return;
    }

    // Check if user has permanently dismissed the banner or already subscribed
    const bannerPermanentlyDismissed = localStorage.getItem(
      "newsletter-banner-dismissed"
    );
    const userAlreadySubscribed = localStorage.getItem("newsletter-subscribed");

    if (
      bannerPermanentlyDismissed === "true" ||
      userAlreadySubscribed === "true"
    ) {
      setShowNewsletterBanner(false);
      return;
    }

    // Show newsletter banner after a longer delay to be less intrusive
    const timer = setTimeout(() => {
      setShowNewsletterBanner(true);
    }, 15000); // Increased from 8 seconds to 15 seconds to be less intrusive

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleCloseBanner = () => {
    setShowNewsletterBanner(false);
    // Remember that user dismissed the banner permanently
    localStorage.setItem("newsletter-banner-dismissed", "true");
  };

  // SEO globals
  const { i18n } = useTranslation();
  const baseUrl = "https://vettconsult.com";
  const supportedLngs = ["en", "es", "fr"];
  const hasLangPrefix = supportedLngs.some(
    (lng) =>
      location.pathname === `/${lng}` ||
      location.pathname.startsWith(`/${lng}/`)
  );
  const canonicalPath = hasLangPrefix
    ? location.pathname
    : `/${i18n.language}${location.pathname === "/" ? "" : location.pathname}`;
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  const ogLocaleMap: Record<string, string> = {
    en: "en_US",
    es: "es_ES",
    fr: "fr_FR",
  };
  const ogLocale = ogLocaleMap[i18n.language] || "en_US";
  const defaultSocialImage = `${baseUrl}/images/logo/LogoVettConsultTransparent.webp`;

  // Build hreflang alternates by stripping any existing language prefix from the path
  const stripLangFromPath = (path: string) => {
    const parts = path.split("/");
    if (parts.length > 1 && supportedLngs.includes(parts[1])) {
      const rest = "/" + parts.slice(2).join("/");
      const cleaned = rest.replace(/\/+$/, "");
      return cleaned === "" ? "/" : cleaned;
    }
    return path || "/";
  };
  const restPath = stripLangFromPath(location.pathname);
  const hreflangLinks = [
    ...supportedLngs.map((lng) => {
      const hrefLangUrl = `${baseUrl}/${lng}${
        restPath === "/" ? "" : restPath
      }`;
      return (
        <link key={lng} rel="alternate" hrefLang={lng} href={hrefLangUrl} />
      );
    }),
    // x-default apunta a la ruta sin prefijo para servir como valor por defecto genérico
    <link
      key="x-default"
      rel="alternate"
      hrefLang="x-default"
      href={`${baseUrl}/en${restPath === "/" ? "" : restPath}`}
    />,
  ];

  const breadcrumbNames: Record<string, string> = {
    "/": "Home",
    "/services": "Services",
    "/pricing": "Pricing",
    "/about": "About",
    "/contact": "Contact",
    "/blog": "Blog",
    "/privacy-policy": "Privacy Policy",
    "/terms": "Terms",
  };
  const breadcrumbSchema =
    restPath === "/"
      ? null
      : {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: `${baseUrl}/${i18n.language}`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: breadcrumbNames[restPath] || "Page",
              item: canonicalUrl,
            },
          ],
        };

  return (
    <div className="flex flex-col min-h-screen" style={{ overflow: "visible" }}>
      <Helmet defaultTitle="VettConsult" titleTemplate="%s | VettConsult">
        <meta name="theme-color" content="#00342d" />
        <meta property="og:site_name" content="VettConsult" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {/* Canonical and social basics */}
        <link rel="canonical" href={canonicalUrl} />
        {hreflangLinks}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={defaultSocialImage} />
        <meta property="og:image:width" content="1563" />
        <meta property="og:image:height" content="1563" />
        <meta name="twitter:image" content={defaultSocialImage} />
        <meta name="twitter:site" content="@vettconsult" />
        <meta name="twitter:creator" content="@vettconsult" />
        <meta property="og:locale" content={ogLocale} />
        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "VettConsult",
            url: baseUrl,
            logo: defaultSocialImage,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            url: baseUrl,
            name: "VettConsult",
          })}
        </script>
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
      </Helmet>
      {/* Solo mostrar Navbar desktop si NO es móvil */}
      {!isMobile && <Navbar />}
      <main
        className="flex-grow"
        style={{
          overflow: "visible",
          marginTop: showNewsletterBanner ? "0" : "0", // Adjust if needed for banner height
        }}
      >
        <Outlet />
      </main>

      {/* Newsletter Banner - Modal Overlay (solo desktop) */}
      {showNewsletterBanner && !isMobile && (
        <NewsletterBanner
          variant="modal"
          onClose={handleCloseBanner}
          showCloseButton={true}
        />
      )}

      {/* Footer solo en desktop, en móvil cada página maneja su propio footer si necesario */}
      {!isMobile && <Footer />}

      {/* Support button solo en desktop */}
      {!isMobile && <SupportStickyButton />}
    </div>
  );
};

export default PublicLayout;
